import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Target,
  Plus,
  GraduationCap,
  Shield,
  Umbrella,
  Home,
  Briefcase,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Coins,
  Sparkles,
  ChevronRight,
  Pencil,
  Trash2,
  Loader2,
  ArrowUpRight,
  Rocket,
  Clock,
  PiggyBank,
  LineChart,
  Award
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOAL_ICONS = {
  education: GraduationCap,
  emergency: Shield,
  retirement: Umbrella,
  home: Home,
  business: Briefcase,
  first_million: Target,
  other: Target
};

const GOAL_COLORS = {
  education: 'from-blue-500 to-blue-600',
  emergency: 'from-emerald-500 to-emerald-600',
  retirement: 'from-purple-500 to-purple-600',
  home: 'from-amber-500 to-amber-600',
  business: 'from-red-500 to-red-600',
  first_million: 'from-cyan-500 to-cyan-600',
  other: 'from-gray-500 to-gray-600'
};

export default function GoalsPlanner() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [goalTypes, setGoalTypes] = useState({});
  const [summary, setSummary] = useState({});
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isContributeDialogOpen, setIsContributeDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    deadline: '',
    goal_type: 'other',
    monthly_contribution: '',
    notes: ''
  });
  const [contributionAmount, setContributionAmount] = useState('');

  const fetchGoals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      setGoals(response.data.goals || []);
      setSummary(response.data.summary || {});
      setGoalTypes(response.data.goal_types || {});
    } catch (error) {
      console.error('Failed to fetch goals:', error);
      toast.error(t('Failed to load goals', 'Imeshindwa kupakia malengo'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const resetForm = () => {
    setFormData({
      name: '',
      target_amount: '',
      deadline: '',
      goal_type: 'other',
      monthly_contribution: '',
      notes: ''
    });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.target_amount || !formData.deadline) {
      toast.error(t('Please fill all required fields', 'Tafadhali jaza sehemu zote zinazohitajika'));
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/goals`, {
        name: formData.name,
        target_amount: parseFloat(formData.target_amount),
        deadline: formData.deadline,
        goal_type: formData.goal_type,
        monthly_contribution: parseFloat(formData.monthly_contribution) || 0,
        notes: formData.notes || null
      });
      toast.success(t('Goal created successfully!', 'Lengo limeundwa kwa mafanikio!'));
      setIsCreateDialogOpen(false);
      resetForm();
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to create goal', 'Imeshindwa kuunda lengo'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedGoal) return;
    
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/goals/${selectedGoal.id}`, {
        name: formData.name || undefined,
        target_amount: formData.target_amount ? parseFloat(formData.target_amount) : undefined,
        deadline: formData.deadline || undefined,
        monthly_contribution: formData.monthly_contribution ? parseFloat(formData.monthly_contribution) : undefined,
        notes: formData.notes || undefined
      });
      toast.success(t('Goal updated successfully!', 'Lengo limesasishwa kwa mafanikio!'));
      setIsEditDialogOpen(false);
      setSelectedGoal(null);
      resetForm();
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to update goal', 'Imeshindwa kusasisha lengo'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedGoal) return;
    
    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/goals/${selectedGoal.id}`);
      toast.success(t('Goal deleted successfully!', 'Lengo limefutwa kwa mafanikio!'));
      setIsDeleteDialogOpen(false);
      setSelectedGoal(null);
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to delete goal', 'Imeshindwa kufuta lengo'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContribute = async () => {
    if (!selectedGoal || !contributionAmount) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/goals/${selectedGoal.id}/contribute?amount=${parseFloat(contributionAmount)}`);
      toast.success(t('Contribution added!', 'Mchango umeongezwa!'));
      setIsContributeDialogOpen(false);
      setSelectedGoal(null);
      setContributionAmount('');
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to add contribution', 'Imeshindwa kuongeza mchango'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (goal) => {
    setSelectedGoal(goal);
    setFormData({
      name: goal.name,
      target_amount: goal.target_amount.toString(),
      deadline: goal.deadline?.split('T')[0] || '',
      goal_type: goal.goal_type,
      monthly_contribution: goal.monthly_contribution?.toString() || '',
      notes: goal.notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
    return amount?.toLocaleString() || '0';
  };

  const getGoalIcon = (goalType) => {
    const Icon = GOAL_ICONS[goalType] || Target;
    return Icon;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Target className="w-10 h-10 text-primary animate-pulse" />
          </div>
          <p className="text-muted-foreground">{t('Loading your goals...', 'Inapakia malengo yako...')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6 pb-8">
        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700 p-6 md:p-8 text-white">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm">
                <Rocket className="w-3 h-3 mr-1" />
                {t('Goal-Based Investing', 'Uwekezaji wa Malengo')}
              </Badge>
            </div>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  {t('Investment Goals Planner', 'Mpangilio wa Malengo ya Uwekezaji')}
                </h1>
                <p className="text-white/60 max-w-lg">
                  {t('Set financial goals, track progress, and let AI guide you to achieve them faster.',
                     'Weka malengo ya kifedha, fuatilia maendeleo, na acha AI ikuongoze kuyafikia haraka zaidi.')}
                </p>
              </div>
              
              <Button 
                onClick={() => setIsCreateDialogOpen(true)}
                className="bg-white text-slate-900 hover:bg-white/90 rounded-full"
                data-testid="create-goal-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('Create New Goal', 'Unda Lengo Jipya')}
              </Button>
            </div>

            {/* Summary Stats */}
            {summary.total_goals > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-white/10">
                <div className="text-center">
                  <p className="text-3xl font-bold">{summary.total_goals}</p>
                  <p className="text-white/60 text-sm">{t('Total Goals', 'Malengo Jumla')}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-400">{summary.on_track_count}</p>
                  <p className="text-white/60 text-sm">{t('On Track', 'Sawa')}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">TZS {formatCurrency(summary.total_target)}</p>
                  <p className="text-white/60 text-sm">{t('Target', 'Lengo')}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">{summary.overall_progress}%</p>
                  <p className="text-white/60 text-sm">{t('Progress', 'Maendeleo')}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Goals Grid */}
        {goals.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {goals.map((goal) => {
              const GoalIcon = getGoalIcon(goal.goal_type);
              const colorClass = GOAL_COLORS[goal.goal_type] || GOAL_COLORS.other;
              
              return (
                <Card 
                  key={goal.id} 
                  className="border-border/50 overflow-hidden hover:border-primary/30 transition-colors group"
                  data-testid={`goal-card-${goal.id}`}
                >
                  {/* Header with gradient */}
                  <div className={`bg-gradient-to-r ${colorClass} p-4 text-white`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <GoalIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{goal.name}</h3>
                          <p className="text-white/80 text-sm">
                            {goalTypes[goal.goal_type]?.name_en || goal.goal_type}
                          </p>
                        </div>
                      </div>
                      {goal.on_track ? (
                        <Badge className="bg-white/20 text-white border-0">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {t('On Track', 'Sawa')}
                        </Badge>
                      ) : (
                        <Badge className="bg-white/20 text-amber-200 border-0">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {t('Behind', 'Nyuma')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <CardContent className="p-4 space-y-4">
                    {/* Progress */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">{t('Progress', 'Maendeleo')}</span>
                        <span className="font-bold text-foreground">{goal.progress_percent}%</span>
                      </div>
                      <Progress value={goal.progress_percent} className="h-3" />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>TZS {formatCurrency(goal.current_amount)}</span>
                        <span>TZS {formatCurrency(goal.target_amount)}</span>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                          <Clock className="w-3 h-3" />
                          {t('Time Left', 'Muda Uliobaki')}
                        </div>
                        <p className="font-semibold text-foreground">
                          {goal.months_remaining} {t('months', 'miezi')}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                          <Coins className="w-3 h-3" />
                          {t('Need/Month', 'Unahitaji/Mwezi')}
                        </div>
                        <p className="font-semibold text-foreground">
                          TZS {formatCurrency(goal.required_monthly)}
                        </p>
                      </div>
                    </div>

                    {/* Monthly Contribution */}
                    {goal.monthly_contribution > 0 && (
                      <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <div className="flex items-center gap-2">
                          <PiggyBank className="w-4 h-4 text-primary" />
                          <span className="text-sm text-foreground">{t('Monthly Saving', 'Akiba ya Mwezi')}</span>
                        </div>
                        <span className="font-semibold text-primary">
                          TZS {formatCurrency(goal.monthly_contribution)}
                        </span>
                      </div>
                    )}

                    {/* Deadline */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{t('Deadline', 'Mwisho')}: {new Date(goal.deadline).toLocaleDateString()}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-border/50">
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="flex-1 rounded-full"
                        onClick={() => {
                          setSelectedGoal(goal);
                          setIsContributeDialogOpen(true);
                        }}
                        data-testid={`contribute-btn-${goal.id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('Add Funds', 'Ongeza Fedha')}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(goal)}
                        data-testid={`edit-goal-${goal.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setSelectedGoal(goal);
                          setIsDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-goal-${goal.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          /* Empty State */
          <Card className="border-dashed border-2">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-6">
                <Target className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {t('No Investment Goals Yet', 'Bado Hakuna Malengo ya Uwekezaji')}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {t('Set your first financial goal and let us help you achieve it with AI-powered recommendations.',
                   'Weka lengo lako la kwanza la kifedha na tuache tukusaidie kulifikia na mapendekezo ya AI.')}
              </p>
              <Button 
                onClick={() => setIsCreateDialogOpen(true)}
                className="rounded-full"
                data-testid="create-first-goal-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('Create Your First Goal', 'Unda Lengo Lako la Kwanza')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card 
            className="border-border/50 cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => navigate('/intelligence')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                <LineChart className="w-7 h-7 text-emerald-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">
                  {t('Portfolio Intelligence', 'Akili ya Portfolio')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('Check your portfolio health score', 'Angalia alama ya afya ya portfolio yako')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
          
          <Card 
            className="border-border/50 cursor-pointer hover:border-primary/30 transition-colors"
            onClick={() => navigate('/rebalance')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center">
                <Award className="w-7 h-7 text-purple-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">
                  {t('Smart Rebalancing', 'Urekebishaji wa Akili')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('Get AI suggestions to optimize your portfolio', 'Pata mapendekezo ya AI kuboresha portfolio yako')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Goal Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              {t('Create New Goal', 'Unda Lengo Jipya')}
            </DialogTitle>
            <DialogDescription>
              {t('Set a financial goal and we\'ll help you track it', 'Weka lengo la kifedha na tutakusaidia kulifuatilia')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="goal-name">{t('Goal Name', 'Jina la Lengo')} *</Label>
              <Input
                id="goal-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('e.g. Emergency Fund', 'mfano Akiba ya Dharura')}
                data-testid="goal-name-input"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="goal-type">{t('Goal Type', 'Aina ya Lengo')}</Label>
              <Select
                value={formData.goal_type}
                onValueChange={(value) => setFormData({ ...formData, goal_type: value })}
              >
                <SelectTrigger data-testid="goal-type-select">
                  <SelectValue placeholder={t('Select goal type', 'Chagua aina ya lengo')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="emergency">{t('Emergency Fund', 'Akiba ya Dharura')}</SelectItem>
                  <SelectItem value="education">{t('Education', 'Elimu')}</SelectItem>
                  <SelectItem value="home">{t('Home Purchase', 'Kununua Nyumba')}</SelectItem>
                  <SelectItem value="retirement">{t('Retirement', 'Kustaafu')}</SelectItem>
                  <SelectItem value="business">{t('Business', 'Biashara')}</SelectItem>
                  <SelectItem value="first_million">{t('First Million', 'Milioni ya Kwanza')}</SelectItem>
                  <SelectItem value="other">{t('Other', 'Nyingine')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="target-amount">{t('Target Amount (TZS)', 'Kiasi cha Lengo (TZS)')} *</Label>
                <Input
                  id="target-amount"
                  type="number"
                  value={formData.target_amount}
                  onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                  placeholder="e.g. 5000000"
                  min="0"
                  data-testid="target-amount-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="deadline">{t('Deadline', 'Mwisho')} *</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="deadline-input"
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="monthly-contribution">
                {t('Monthly Contribution (TZS)', 'Mchango wa Kila Mwezi (TZS)')}
              </Label>
              <Input
                id="monthly-contribution"
                type="number"
                value={formData.monthly_contribution}
                onChange={(e) => setFormData({ ...formData, monthly_contribution: e.target.value })}
                placeholder={t('How much will you save monthly?', 'Utaweka akiba ngapi kila mwezi?')}
                min="0"
                data-testid="monthly-contribution-input"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="notes">{t('Notes (Optional)', 'Maelezo (Si lazima)')}</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('Any additional notes...', 'Maelezo yoyote ya ziada...')}
                data-testid="notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={isSubmitting}>
              {t('Cancel', 'Ghairi')}
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting} data-testid="submit-goal-btn">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Creating...', 'Inatengeneza...')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Create Goal', 'Unda Lengo')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              {t('Edit Goal', 'Hariri Lengo')}
            </DialogTitle>
            <DialogDescription>
              {t('Update your goal details', 'Sasisha maelezo ya lengo lako')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">{t('Goal Name', 'Jina la Lengo')}</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-goal-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-target">{t('Target Amount (TZS)', 'Kiasi cha Lengo (TZS)')}</Label>
                <Input
                  id="edit-target"
                  type="number"
                  value={formData.target_amount}
                  onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                  min="0"
                  data-testid="edit-target-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-deadline">{t('Deadline', 'Mwisho')}</Label>
                <Input
                  id="edit-deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  data-testid="edit-deadline-input"
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-monthly">{t('Monthly Contribution (TZS)', 'Mchango wa Kila Mwezi (TZS)')}</Label>
              <Input
                id="edit-monthly"
                type="number"
                value={formData.monthly_contribution}
                onChange={(e) => setFormData({ ...formData, monthly_contribution: e.target.value })}
                min="0"
                data-testid="edit-monthly-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={isSubmitting}>
              {t('Cancel', 'Ghairi')}
            </Button>
            <Button onClick={handleEdit} disabled={isSubmitting} data-testid="save-edit-goal-btn">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Saving...', 'Inahifadhi...')}
                </>
              ) : (
                t('Save Changes', 'Hifadhi Mabadiliko')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Delete Goal?', 'Futa Lengo?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('Are you sure you want to delete', 'Una uhakika unataka kufuta')} "{selectedGoal?.name}"? 
              {t(' This action cannot be undone.', ' Kitendo hiki hakiwezi kutenduliwa.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>{t('Cancel', 'Ghairi')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-goal-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Deleting...', 'Inafuta...')}
                </>
              ) : (
                t('Delete', 'Futa')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Contribute Dialog */}
      <Dialog open={isContributeDialogOpen} onOpenChange={setIsContributeDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-primary" />
              {t('Add Contribution', 'Ongeza Mchango')}
            </DialogTitle>
            <DialogDescription>
              {t('Add funds to', 'Ongeza fedha kwa')} "{selectedGoal?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedGoal && (
              <div className="p-4 rounded-xl bg-muted/50 mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">{t('Current', 'Sasa')}</span>
                  <span className="font-medium">TZS {formatCurrency(selectedGoal.current_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('Target', 'Lengo')}</span>
                  <span className="font-medium">TZS {formatCurrency(selectedGoal.target_amount)}</span>
                </div>
              </div>
            )}
            
            <div className="grid gap-2">
              <Label htmlFor="contribution-amount">{t('Amount (TZS)', 'Kiasi (TZS)')}</Label>
              <Input
                id="contribution-amount"
                type="number"
                value={contributionAmount}
                onChange={(e) => setContributionAmount(e.target.value)}
                placeholder={t('Enter amount', 'Ingiza kiasi')}
                min="0"
                data-testid="contribution-amount-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsContributeDialogOpen(false)} disabled={isSubmitting}>
              {t('Cancel', 'Ghairi')}
            </Button>
            <Button 
              onClick={handleContribute} 
              disabled={isSubmitting || !contributionAmount}
              data-testid="submit-contribution-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Adding...', 'Inaongeza...')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Add Contribution', 'Ongeza Mchango')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
